/**
 * What survives a schema refresh, and why the open document has to.
 *
 * When a job's fields change, the batch on screen is re-read against the new
 * contract. The reply carries every document freshly extracted. The renderer
 * cannot keep all of them — holding thousands of full readings in memory is the
 * whole reason the paged projection exists — so all but one drop to skeletons.
 *
 * The one that stays is the one the operator is looking at. That is not a
 * nicety. Re-hydration runs from the document-selection handler, so a skeleton
 * becomes whole again when it is SELECTED; but the open document is already
 * selected, and opening a job selects it before this refresh comes back. No
 * further selection ever fires for it. Dropping it therefore did not defer its
 * loading, it cancelled it: the operator sat in front of a document with every
 * field blank, on a screen that had just been handed that document's values,
 * and it stayed blank until they clicked to another document and back.
 *
 * Keeping it is not the same as HAVING it. Past the projection threshold the
 * engine sends skeletons of its own, so the document this rule keeps can arrive
 * already stripped. Kept-and-empty is worse than dropped: "loaded" is precisely
 * what stops anything downstream from fetching it, so the operator gets a type
 * on the left and nothing on the right, permanently. `hasContent` is how the
 * caller tells this rule the difference; without it, the rule can only see ids.
 *
 * Keeping it costs nothing. This copy arrived in the same reply as the others
 * and was extracted moments ago, so it is the NEWEST reading in the process —
 * not a stale hydrate held over from before the schema changed, which is the
 * thing the drop-everything rule was right to be afraid of.
 */

export type Identified = { readonly id: string };

export type RefreshedProjection<TDocument extends Identified> = {
  readonly documents: readonly TDocument[];
  /** The ids the renderer may treat as fully read. Never claims more than it kept. */
  readonly loadedDocumentIds: readonly string[];
};

/**
 * `selectedDocument` is an index into the refreshed list and is clamped, because
 * a refresh can return fewer documents than were on screen when it was sent.
 */
export const projectRefreshedBatch = <TDocument extends Identified>(
  documents: readonly TDocument[],
  selectedDocument: number,
  toSkeleton: (document: TDocument) => TDocument,
  /** Whether a document actually carries a reading. Assumed true when unknown. */
  hasContent: (document: TDocument) => boolean = () => true,
): RefreshedProjection<TDocument> => {
  if (documents.length === 0) return { documents: [], loadedDocumentIds: [] };

  const keptIndex = Math.min(Math.max(0, Math.trunc(selectedDocument)), documents.length - 1);
  const keptId = documents[keptIndex].id;

  return {
    documents: documents.map((document) =>
      document.id === keptId ? document : toSkeleton(document),
    ),
    // Exactly what was kept AND arrived whole, so nothing downstream can decide
    // a skeleton has already been read and refuse to fetch it. An empty list
    // here is not a loss: it is the one signal that makes the caller go and get
    // the document, which is the only way it ever fills in.
    loadedDocumentIds: hasContent(documents[keptIndex]) ? [keptId] : [],
  };
};
